import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Button,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemAvatar,
  ListItemText,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import NotificationsIcon from "@material-ui/icons/Notifications";
import api from "../services/api";
import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import BirthdayModal from "../components/BirthdayModal";
import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import VersionControl from "../components/VersionControl";
import TermsAcceptModal from "../components/TermsAcceptModal";
import useSocketListener from "../hooks/useSocketListener";
import { logInfo, logError } from "../utils/logger";
import { getTokens, brandAlpha, INK, TOPBAR } from "../styles/tokens";
import { getMenuTrail } from "./menuTrail";
import { UI_ICONS } from "./menuIcons";

const backendUrl = getBackendUrl();
const drawerWidth = 238;
const drawerWidthCollapsed = 76;
const drawerWidthMobile = 264;
// O que as telas de altura cheia descontam da <main> (ver appBarSpacer)
const PAGE_OFFSET = 64;
const PAGE_OFFSET_COMPACT = 56;

const PAGE_TITLES = [
  ["/tickets", "Central de atendimento"],
  ["/contacts", "Contatos"],
  ["/quick-messages", "Respostas rápidas"],
  ["/kanban", "Kanban"],
  ["/schedules", "Agendamentos"],
  ["/tags", "Tags"],
  ["/chats", "Chat interno"],
  ["/campaigns-config", "Configurações de campanhas"],
  ["/campaigns", "Campanhas"],
  ["/campaign", "Relatório da campanha"],
  ["/contact-lists", "Listas de contatos"],
  ["/invoice-campaigns", "Cobranças de campanhas"],
  ["/meta-templates", "Modelos do WhatsApp"],
  ["/billing-rules", "Regras de cobrança"],
  ["/flowbuilders", "Flowbuilder"],
  ["/flowbuilder", "Editor do fluxo"],
  ["/phrase-lists", "Frases do fluxo"],
  ["/reports", "Relatórios"],
  ["/relatorio-vendas", "Relatório de vendas"],
  ["/moments", "Atendimento em tempo real"],
  ["/supervision", "Supervisão"],
  ["/connections", i18n.t("connections.title")],
  ["/allconnections", "Todos os canais"],
  ["/queues", "Filas"],
  ["/queue-integration", "Integrações de filas"],
  ["/users", "Usuários"],
  ["/settings", "Configurações"],
  ["/helps", "Central de ajuda"],
  ["/announcements", "Informativos"],
  ["/messages-api", "API"],
  ["/network-alerts", "Alertas de rede"],
  ["/todolist", "Tarefas"],
  ["/files", "Arquivos"],
  ["/wallets", "Carteiras"],
  ["/prompts", "Prompts"],
  ["/tagskanban", "Quadro de tags"],
  ["/call-historicals", "Histórico de ligações"],
  ["/birthday-settings", "Aniversários"],
  ["/financeiro-aberto", "Financeiro em aberto"],
  ["/financeiro", "Financeiro"],
  ["/companies", "Empresas"],
  ["/reseller-finance", "Financeiro de revenda"],
  ["/audit-logs", "Auditoria"],
];

const getPageTitle = (pathname) => {
  if (!pathname || pathname === "/") return "Visão geral";
  const normalizedPath = pathname.toLowerCase();
  const match = PAGE_TITLES.find(
    ([route]) => normalizedPath === route || normalizedPath.startsWith(`${route}/`)
  );
  if (match) return match[1];

  const segment = normalizedPath.split("/").filter(Boolean)[0] || "Visão geral";
  return segment
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const useStyles = makeStyles((theme) => {
  const mode = theme.palette.type;
  const isDark = mode === "dark";
  const brand = theme.palette.primary.main;
  const t = getTokens(mode, brand);
  // Menu lateral: tinta e destaque derivados das duas cores da empresa (App.js)
  const nav = (theme.tokens && theme.tokens.nav) || t.nav;
  const iconInk = isDark ? t.textSecondary : INK[600];
  const strongInk = isDark ? t.text : INK[800];
  // preenchimento suave das pílulas da barra (a barra é `paper`)
  const softFill = isDark ? "rgba(255,255,255,0.06)" : t.page;
  // a barra superior usa a mesma tinta do menu lateral: os dois formam uma moldura só
  const barFill = "rgba(255,255,255,0.08)";
  const barFillHover = "rgba(255,255,255,0.15)";
  const sharpTransition = (props) =>
    theme.transitions.create(props, {
      easing: theme.transitions.easing.sharp,
      duration: 200,
    });

  return {
    root: {
      display: "flex",
      height: "100vh",
      minWidth: 0,
      overflow: "hidden",
      [theme.breakpoints.down("sm")]: {
        height: "100dvh",
      },
      backgroundColor: t.page,
      "& .MuiButton-outlinedPrimary": {
        color: brand,
        border: `1px solid ${brandAlpha(brand, 0.4)}`,
        borderRadius: t.radius.sm,
        fontWeight: 600,
        textTransform: "none",
        transition: "background-color 150ms ease, border-color 150ms ease",
        "&:hover": {
          backgroundColor: t.tint.soft,
          borderColor: brand,
        },
      },
      "& .MuiTab-textColorPrimary.Mui-selected": {
        color: brand,
        fontWeight: 600,
      },
    },

    avatar: {
      width: "100%",
    },

    // ===== Barra superior: 70px, na tinta do menu lateral (moldura contínua), sem sombra =====
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      // altura fechada na barra (o filete entra na conta: border-box)
      height: TOPBAR.height,
      background: nav.bg,
      borderBottom: `1px solid ${nav.line}`,
      boxShadow: "none",
      color: nav.textStrong,
      transition: sharpTransition(["width", "margin"]),
      [theme.breakpoints.down("sm")]: {
        height: TOPBAR.compact,
        zIndex: theme.zIndex.appBar,
      },
    },

    appBarShift: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: sharpTransition(["width", "margin"]),
      [theme.breakpoints.down("sm")]: {
        marginLeft: 0,
        width: "100%",
      },
    },

    appBarCollapsed: {
      marginLeft: drawerWidthCollapsed,
      width: `calc(100% - ${drawerWidthCollapsed}px)`,
      transition: sharpTransition(["width", "margin"]),
      [theme.breakpoints.down("sm")]: {
        marginLeft: 0,
        width: "100%",
      },
    },

    toolbar: {
      minHeight: 0,
      height: "100%",
      paddingLeft: 28,
      paddingRight: 20,
      gap: 20,
      color: nav.textStrong,
      background: "transparent",
      [theme.breakpoints.down("sm")]: {
        minHeight: 0,
        paddingLeft: 12,
        paddingRight: 8,
        gap: 8,
      },
    },

    menuButtonHidden: {
      display: "none",
    },

    // Trilha: <grupo do menu> › <tela>; no celular só a tela
    titleBlock: {
      flex: "0 1 auto",
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      [theme.breakpoints.down("sm")]: {
        flex: 1,
      },
    },

    pageTitle: {
      margin: 0,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontFamily: t.font,
      fontSize: 14.5,
      fontWeight: 500,
      lineHeight: 1.3,
      letterSpacing: 0,
      color: nav.text,
      whiteSpace: "nowrap",
      "& > span": {
        flex: "none",
      },
      "& > svg": {
        flex: "none",
        fontSize: 18,
        color: nav.textMuted,
      },
      "& > b": {
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontSize: 15.5,
        fontWeight: 650,
        letterSpacing: "-0.005em",
        color: nav.textStrong,
      },
      [theme.breakpoints.down("sm")]: {
        "& > span, & > svg": { display: "none" },
        "& > b": { fontSize: 16.5 },
      },
    },

    // base 0: a busca só ocupa a sobra — quem encolhe por último é o título
    searchArea: {
      flex: "1 1 0",
      maxWidth: 400,
      minWidth: 200,
      marginLeft: 12,
      [theme.breakpoints.down("md")]: { display: "none" },
    },

    // Mesma pílula da busca do painel de conversas
    searchPill: {
      height: 40,
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "0 8px 0 14px",
      borderRadius: 999,
      border: "1px solid transparent",
      background: barFill,
      color: nav.text,
      font: "inherit",
      fontSize: 13.5,
      textAlign: "left",
      cursor: "text",
      transition: "border-color 160ms ease, background-color 160ms ease",
      "& svg": { fontSize: 18, flexShrink: 0, color: nav.textMuted },
      "&:hover": {
        background: barFillHover,
      },
      "&:focus-visible, &:focus": {
        outline: "none",
        background: barFillHover,
        borderColor: nav.accent,
      },
      "& span": {
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& kbd": {
        flex: "none",
        height: 24,
        padding: "0 8px",
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        border: `1px solid ${nav.line}`,
        background: "transparent",
        color: nav.textMuted,
        fontFamily: t.font,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.02em",
      },
    },

    // ===== Menu lateral: tinta sólida da marca, 238px aberto / 76px recolhido =====
    drawerPaper: {
      position: "relative",
      whiteSpace: "nowrap",
      width: drawerWidth,
      transition: sharpTransition("width"),
      willChange: "width",
      overflowX: "hidden",
      overflowY: "hidden",
      background: nav.bg,
      color: nav.text,
      // no escuro a tinta fica próxima do fundo da página: o filete separa
      borderRight: isDark ? `1px solid ${t.line}` : "none",
      boxShadow: "none",
      display: "flex",
      flexDirection: "column",
      [theme.breakpoints.down("sm")]: {
        width: drawerWidthMobile,
      },
    },

    drawerPaperClose: {
      overflowX: "hidden",
      overflowY: "hidden",
      transition: sharpTransition("width"),
      width: drawerWidthCollapsed,
      [theme.breakpoints.up("sm")]: {
        width: drawerWidthCollapsed,
      },
    },

    // Cabeçalho do menu: marca e empresa, na altura da barra superior
    toolbarIcon: {
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
      gap: 11,
      height: TOPBAR.height,
      minHeight: TOPBAR.height,
      padding: "0 12px 0 20px",
      overflow: "hidden",
    },

    toolbarIconCollapsed: {
      padding: "0 20px",
      "& $brandText, & $drawerToggle": {
        display: "none",
      },
    },

    brandBox: {
      flexShrink: 0,
      width: 36,
      height: 36,
      borderRadius: t.radius.sm,
      background: "rgba(255,255,255,0.08)",
      color: nav.accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      overflow: "hidden",
      "& img": {
        width: 24,
        height: 24,
        objectFit: "contain",
      },
    },

    brandText: {
      flex: 1,
      minWidth: 0,
      lineHeight: 1.2,
      "& p": {
        margin: 0,
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: "-0.01em",
        color: nav.textStrong,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& span": {
        display: "block",
        marginTop: 3,
        fontSize: 10,
        fontWeight: 600,
        color: nav.textMuted,
        textTransform: "uppercase",
        letterSpacing: ".06em",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },

    drawerToggle: {
      width: 30,
      height: 30,
      padding: 5,
      color: nav.textMuted,
      borderRadius: t.radius.sm,
      "&:hover": {
        backgroundColor: nav.hover,
        color: nav.textStrong,
      },
      "& svg": {
        fontSize: 20,
      },
    },

    // As telas de altura cheia (Atendimento) descontam da <main> a altura antiga
    // da barra — 64px, ou 56px abaixo de 960px. A barra agora tem 70px: os 6px
    // que faltam entram como padding da <main> e o espaçador mantém os 64/56
    // que as telas já conhecem. Nenhuma tela precisa mudar a conta.
    appBarSpacer: {
      minHeight: PAGE_OFFSET,
      [theme.breakpoints.down("sm")]: { minHeight: PAGE_OFFSET_COMPACT },
    },

    content: {
      flex: 1,
      overflow: "auto",
      padding: 0,
      paddingTop: TOPBAR.height - PAGE_OFFSET,
      margin: 0,
      minWidth: 0,
      background: t.page,
      [theme.breakpoints.down("sm")]: {
        paddingTop: TOPBAR.compact - PAGE_OFFSET_COMPACT,
      },
    },

    container: {
      padding: 0,
      margin: 0,
      maxWidth: "none",
      width: "100%",
    },

    containerWithScroll: {
      flex: 1,
      overflowY: "auto",
      overflowX: "hidden",
      // 6 + 4 da barra de rolagem (sempre reservada) = os mesmos 10 da esquerda;
      // largura fixa dos itens, com ou sem rolagem, para os rótulos não cortarem
      padding: "6px 6px 14px 10px",
      scrollbarGutter: "stable",
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        borderRadius: 999,
        background: "rgba(255,255,255,.2)",
      },
      // Só no Firefox: no Chrome (121+) estas propriedades anulam o ::-webkit-scrollbar acima
      "@supports not selector(::-webkit-scrollbar)": {
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(255,255,255,.2) transparent",
      },
    },

    // Rodapé do menu: conta e selo "Ambiente protegido"
    drawerFooter: {
      flexShrink: 0,
      display: "grid",
      gap: 10,
      padding: "12px 14px 16px",
      borderTop: `1px solid ${nav.line}`,
    },

    drawerFooterCollapsed: {
      padding: "10px 10px 12px",
    },

    drawerUserCard: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minHeight: 44,
      padding: "5px 8px",
      borderRadius: t.radius.sm,
      background: "transparent",
      border: 0,
      overflow: "hidden",
      transition: "background-color 160ms ease",
      width: "100%",
      color: "inherit",
      font: "inherit",
      textAlign: "left",
      cursor: "pointer",
      "&:hover": {
        background: nav.hover,
      },
      "&:focus-visible": {
        outline: `2px solid ${nav.accent}`,
        outlineOffset: -2,
      },
    },

    drawerUserCardCollapsed: {
      padding: 2,
      justifyContent: "center",
      "& $drawerUserInfo": {
        display: "none",
      },
    },

    drawerUserAvatar: {
      width: 32,
      height: 32,
      flexShrink: 0,
      background: nav.accent,
      color: nav.bg,
      fontSize: 13,
      fontWeight: 700,
    },

    drawerUserInfo: {
      flex: 1,
      minWidth: 0,
      lineHeight: 1.25,
      "& p": {
        margin: 0,
        fontSize: 13,
        fontWeight: 600,
        color: nav.textStrong,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& span": {
        display: "block",
        fontSize: 11,
        marginTop: 2,
        color: nav.textMuted,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },

    drawerSecurity: {
      display: "flex",
      alignItems: "center",
      gap: 7,
      padding: "0 8px",
      color: nav.textMuted,
      fontSize: 11,
      fontWeight: 400,
      lineHeight: 1.4,
      "& svg": {
        fontSize: 15,
        color: nav.accent,
      },
    },

    NotificationsPopOver: {
      // Mantém original
    },

    // Ações da barra: utilidades, comunicação e conta
    avatar2: {
      width: 36,
      height: 36,
      borderRadius: "50%",
      background: barFillHover,
      color: "#ffffff",
      fontSize: 13.5,
      fontWeight: 700,
    },

    updateDiv: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },

    toolbarActions: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexShrink: 0,
      marginLeft: "auto",
      [theme.breakpoints.down("xs")]: { gap: 2 },
    },

    // Utilidades (idioma, tema, som, recarregar) numa cápsula só
    utilityActions: {
      display: "flex",
      alignItems: "center",
      gap: 2,
      padding: 3,
      borderRadius: 999,
      background: barFill,
      color: nav.text,
      "& .MuiIconButton-root, & .language-dropdown > button": {
        width: 34,
        height: 34,
        padding: 0,
        borderRadius: "50%",
        color: nav.text,
        transition: "background-color 150ms ease, color 150ms ease",
        "&:hover": {
          backgroundColor: barFillHover,
          color: nav.textStrong,
          boxShadow: "none",
        },
        "& svg": { fontSize: 19 },
      },
    },

    utilityMoreButton: {
      display: "none",
      [theme.breakpoints.down("sm")]: {
        display: "inline-flex",
      },
    },

    mobileUtilityMenu: {
      display: "flex",
      alignItems: "center",
      gap: 3,
      padding: 6,
      color: iconInk,
      "& .MuiIconButton-root": {
        width: 36,
        height: 36,
        padding: 7,
        borderRadius: "50%",
        color: iconInk,
      },
    },

    // Comunicação (conversas não lidas, informativos, chat interno)
    communicationActions: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      "& .MuiIconButton-root": {
        width: 40,
        height: 40,
        padding: 0,
        borderRadius: "50%",
        color: nav.text,
        transition: "background-color 150ms ease, color 150ms ease",
        "&:hover": {
          backgroundColor: barFill,
          color: nav.textStrong,
        },
        "& svg": { fontSize: 22 },
      },
      // contador com anel na cor da barra: não "flutua" sobre o ícone
      "& .MuiBadge-badge": {
        boxShadow: `0 0 0 2px ${nav.bg}`,
        fontWeight: 700,
      },
      "& .MuiBadge-badge:not(.MuiBadge-dot)": {
        minWidth: 18,
        height: 18,
        padding: "0 5px",
        fontSize: 10.5,
        borderRadius: 999,
      },
      [theme.breakpoints.down("xs")]: {
        gap: 0,
        "& .MuiIconButton-root": { width: 36, height: 36 },
      },
    },

    actionsDivider: {
      width: 1,
      height: 28,
      background: nav.line,
      [theme.breakpoints.down("sm")]: { display: "none" },
    },

    profileButton: {
      minWidth: 0,
      height: 46,
      padding: "0 10px 0 5px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      border: "1px solid transparent",
      borderRadius: 999,
      background: "transparent",
      color: nav.textStrong,
      cursor: "pointer",
      font: "inherit",
      transition: "background-color 150ms ease, border-color 150ms ease",
      "&:hover": {
        background: barFill,
      },
      "&:focus-visible": {
        outline: "none",
        borderColor: nav.accent,
      },
      // ponto "online": anel na cor da barra
      "& .MuiBadge-badge": {
        boxShadow: `0 0 0 2px ${nav.bg}`,
      },
      [theme.breakpoints.down("md")]: {
        padding: 4,
        height: 44,
      },
    },

    profileMeta: {
      minWidth: 0,
      maxWidth: 168,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      lineHeight: 1.15,
      "& strong": {
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: 13,
        fontWeight: 600,
        color: nav.textStrong,
      },
      "& span": {
        maxWidth: "100%",
        marginTop: 3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: nav.textMuted,
        fontSize: 11,
        fontWeight: 500,
      },
      [theme.breakpoints.down("md")]: { display: "none" },
    },

    profileChevron: {
      fontSize: 18,
      color: nav.textMuted,
      [theme.breakpoints.down("md")]: { display: "none" },
    },

    toolbarButton: {
      width: 36,
      height: 36,
      color: nav.text,
      borderRadius: "50%",
      padding: 8,
      margin: 0,
      transition: "background-color 150ms ease, color 150ms ease",
      "&:hover": {
        backgroundColor: barFill,
        color: nav.textStrong,
      },
      "& svg": {
        fontSize: 20,
      },
    },

    menuButton: {
      width: 38,
      height: 38,
      padding: 8,
      borderRadius: "50%",
      color: nav.text,
      background: barFill,
      "&:hover": {
        backgroundColor: barFillHover,
        color: nav.textStrong,
      },      "& svg": { fontSize: 20 },
    },

    // Seletor de idioma: botão discreto + cartão suspenso
    languageSelector: {
      position: "relative",
      display: "inline-block",
      "& > button": {
        background: "transparent",
        border: "none",
        width: 34,
        height: 34,
        display: "grid",
        placeItems: "center",
        borderRadius: "50%",
        color: iconInk,
        lineHeight: 0,
        padding: 0,
        margin: 0,
        cursor: "pointer",
        transition: "background-color 150ms ease, color 150ms ease",
        "& svg": { fontSize: 19 },
        "&:hover": {
          background: t.muted,
          color: strongInk,
        },
      },
      "& > div": {
        position: "absolute",
        top: 46,
        right: 0,
        background: t.paper,
        border: `1px solid ${t.line}`,
        boxShadow: t.shadow.lift,
        borderRadius: t.radius.md,
        padding: 6,
        zIndex: 1000,
        minWidth: 160,
        "& button": {
          background: "none",
          border: "none",
          color: t.text,
          display: "block",
          width: "100%",
          padding: "8px 12px",
          textAlign: "left",
          borderRadius: t.radius.badge,
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
          transition: "background-color 150ms ease, color 150ms ease",
          "&:hover": {
            background: t.muted,
            color: t.text,
          },
        },
      },
    },

    // Menu da conta
    menuPaper: {
      marginTop: 8,
      background: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.lift,
      "& .MuiList-root": { padding: 6 },
      "& .MuiMenuItem-root": {
        gap: 10,
        fontSize: 13.5,
        fontWeight: 500,
        color: t.text,
        borderRadius: t.radius.badge,
        minHeight: 38,
        padding: "0 10px",
        "& svg": { fontSize: 19, color: t.textMuted },
        "&:hover": {
          backgroundColor: t.muted,
        },
      },
    },

    menuDanger: {
      color: `${t.semantic.error} !important`,
      "& svg": { color: `${t.semantic.error} !important` },
    },

    menuAccount: {
      padding: "8px 10px 12px",
      margin: "0 0 6px",
      borderBottom: `1px solid ${t.line}`,
      outline: "none",
      lineHeight: 1.35,
      listStyle: "none",
      "& > strong": {
        display: "block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: 14,
        fontWeight: 700,
        color: strongInk,
      },
      "& > span": {
        display: "block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: 12,
        color: t.textSecondary,
      },
    },

    // Empresa, plano e validade (saíram da barra)
    menuPlan: {
      marginTop: 10,
      padding: "9px 11px",
      display: "flex",
      flexDirection: "column",
      gap: 2,
      borderRadius: t.radius.sm,
      background: softFill,
      "& b, & span": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& b": { fontSize: 12.5, fontWeight: 700, color: strongInk },
      "& span": { fontSize: 12, color: t.textSecondary },
      "& em": {
        alignSelf: "flex-start",
        marginTop: 5,
        padding: "2px 8px",
        borderRadius: 999,
        fontStyle: "normal",
        fontSize: 11,
        fontWeight: 600,
        color: t.status.ok.fg,
        background: t.status.ok.bg,
      },
    },
  };
});

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.main,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}))(Badge);

const LoggedInLayout = ({ children, hideMenu = false }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [utilityAnchorEl, setUtilityAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, user, socket } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isDrawerExpanded = drawerOpen;
  const [drawerVariant, setDrawerVariant] = useState("permanent");

  const [showOptions, setShowOptions] = useState(false);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [ackChecked, setAckChecked] = useState(false);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const isCompactViewport = useMediaQuery(theme.breakpoints.down("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);

  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
      try {
        const { data } = await api.get("/announcements/for-company", {
          params: {
            status: true,
            pageNumber: "1"
          }
        });

        // Filtra apenas os informativos ativos e não expirados
        const activeAnnouncementsRaw = data.records.filter(announcement => {
          const isActive = announcement.status === true || announcement.status === "true";
          const isNotExpired = !announcement.expiresAt || new Date(announcement.expiresAt) > new Date();
          return isActive && isNotExpired;
        });

        // Backend já filtra por empresa excluindo os reconhecidos
        const activeAnnouncements = activeAnnouncementsRaw;
        setAnnouncements(activeAnnouncements);
        setShowAnnouncementsModal(activeAnnouncements.length > 0);
      } catch (err) {
        toastError(err);
      }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchAnnouncements();
    }
  }, [user?.id, fetchAnnouncements]);

  // Atualiza checkbox ao trocar de aviso
  useEffect(() => {
    if (!selectedAnnouncement) {
      setAckChecked(false);
      return;
    }
    // Não precisamos mais ler localStorage; tratamos via backend
    setAckChecked(false);
  }, [selectedAnnouncement, user?.companyId]);

  const handleToggleAcknowledge = async (announcementId, checked) => {
    try {
      if (checked) {
        await api.post(`/announcements/${announcementId}/ack`);
        // Remove este aviso da lista
        setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
        setSelectedAnnouncement(null);
      } else {
        await api.delete(`/announcements/${announcementId}/ack`);
        // Opcional: recarregar lista para reexibir (se desejar permitir desfazer)
        await fetchAnnouncements();
      }
      // Fecha modal se não restarem avisos
      setShowAnnouncementsModal((prev) => {
        return announcements.length - 1 > 0;
      });
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (isCompactViewport) {
      setDrawerVariant("temporary");
      setDrawerOpen(false);
    } else {
      setDrawerVariant("permanent");
      const savedPreference = localStorage.getItem(
        `digiteai:drawer:${user?.id || "default"}`
      );
      setDrawerOpen(
        savedPreference
          ? savedPreference === "expanded"
          : user.defaultMenu !== "closed"
      );
    }
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [isCompactViewport, user.defaultMenu, user?.id]);

  useEffect(() => {
    const companyId = user?.companyId;

    if (companyId) {
      const buildProfileUrl = () => {
        const savedProfileImage = localStorage.getItem("profileImage");
        const currentProfileImage = savedProfileImage || user.profileImage;

        if (currentProfileImage) {
          return `${backendUrl}/public/company${companyId}/user/${currentProfileImage}`;
        }
        return `${backendUrl}/public/app/noimage.png`;
      };

      setProfileUrl(buildProfileUrl());
    }
  }, [user?.companyId, user?.profileImage, backendUrl]);

  // Callbacks dos eventos
  const handleAuthEvent = useCallback((data) => {
    if (data.user.id === +user?.id) {
      toastError("Sua conta foi acessada em outro computador.");
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 1000);
    }
  }, [user?.id]);

  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        localStorage.setItem("profileImage", data.user.profileImage);
      }
    }
  }, [user?.companyId, user?.id, backendUrl]);

  // Callbacks para eventos de aniversário
  const handleUserBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de usuário recebido", { data });
    if (data.userId === +user?.id) {
      setShowBirthdayModal(true);
    }
  }, [user?.id]);

  const handleContactBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de contato recebido", { data });
  }, []);

  // Verificar aniversários no login
  const checkBirthdaysOnLogin = useCallback(async () => {
    if (user?.id && user?.companyId) {
      try {
        const { data } = await api.get("/birthdays/today");
        const birthdayData = data.data;

        // Verificar se o usuário atual faz aniversário hoje
        const userBirthday = birthdayData.users.find(u => u.id === +user.id);
        if (userBirthday) {
          logInfo("Usuário faz aniversário hoje; exibindo modal");
          setShowBirthdayModal(true);
        }

        // Se há aniversariantes, mostrar notificação
        if (birthdayData.users.length > 0 || birthdayData.contacts.length > 0) {
          logInfo("Há aniversariantes hoje", { birthdayData });
        }
      } catch (error) {
        logError("Erro ao verificar aniversários", error);
      }
    }
  }, [user?.id, user?.companyId]);

  // SLA estourado → toast para admin/supervisor com link para o ticket
  const canSeeSla = user?.profile === "admin" || user?.allowRealTime === "enabled";
  const handleSlaEvent = useCallback((data) => {
    if (!canSeeSla || !data || data.action !== "breached") return;
    const contact = data.contactName || i18n.t("sla.toast.unknownContact");
    const queueName = data.queueName || (user?.queues || []).find((q) => q.id === data.queueId)?.name;
    const kind = i18n.t(`sla.kind.${data.kind || "firstResponse"}`);
    const msg = queueName
      ? i18n.t("sla.toast.breachedWithQueue", { contact, queue: queueName, kind })
      : i18n.t("sla.toast.breached", { contact, kind });
    const uuid = data.ticketUuid || data.uuid || (data.ticket && data.ticket.uuid);
    toast.warn(msg, {
      toastId: `sla-${data.ticketId}-${data.kind}`,
      autoClose: 8000,
      onClick: uuid ? () => history.push(`/tickets/${uuid}`) : undefined,
      style: uuid ? { cursor: "pointer" } : undefined,
    });
  }, [canSeeSla, user?.queues, history]);

  // Registrar listeners
  useSocketListener(socket, user, 'auth', handleAuthEvent);
  useSocketListener(socket, user, 'sla', handleSlaEvent, [handleSlaEvent]);
  useSocketListener(socket, user, 'user', handleUserUpdate);
  useSocketListener(socket, user, 'user-birthday', handleUserBirthday);
  useSocketListener(socket, user, 'contact-birthday', handleContactBirthday);

  // Verificar aniversários quando o usuário faz login
  useEffect(() => {
    if (user?.id && user?.companyId) {
      // Pequeno delay para garantir que o socket esteja conectado
      const timer = setTimeout(() => {
        checkBirthdaysOnLogin();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);

  // Status do usuário
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");

      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  const handleUpdateStart = () => {
    setUpdateInProgress(true);
  };

  const handleUpdateComplete = () => {
    setUpdateInProgress(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleDrawerToggle = () => {
    setDrawerOpen((current) => {
      const next = !current;
      if (!isCompactViewport) {
        localStorage.setItem(
          `digiteai:drawer:${user?.id || "default"}`,
          next ? "expanded" : "collapsed"
        );
      }
      return next;
    });
  };

  const drawerClose = () => {
    if (isCompactViewport) {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const focusTicketSearch = () => {
    const inputs = Array.from(document.querySelectorAll("input"));
    const searchInput = inputs.find((input) => {
      const descriptor = `${input.placeholder || ""} ${input.getAttribute("aria-label") || ""}`.toLowerCase();
      return (
        descriptor.includes("buscar atendimento") ||
        descriptor.includes("buscar conversa") ||
        descriptor.includes("buscar mensagens") ||
        descriptor.includes("buscar ticket") ||
        descriptor.includes("search attendance") ||
        descriptor.includes("search conversation") ||
        descriptor.includes("search messages") ||
        descriptor.includes("search ticket")
      );
    });

    if (!searchInput) return false;
    searchInput.focus();
    if (typeof searchInput.select === "function") searchInput.select();
    return true;
  };

  const handleGlobalSearch = () => {
    const focusWhenReady = () => {
      if (!focusTicketSearch()) {
        window.setTimeout(focusTicketSearch, 450);
      }
    };

    if (!location.pathname.toLowerCase().startsWith("/tickets")) {
      history.push("/tickets");
      window.setTimeout(focusWhenReady, 300);
      return;
    }
    focusWhenReady();
  };

  // Ctrl/⌘ + K abre a busca de atendimentos de qualquer tela
  const globalSearchRef = React.useRef(handleGlobalSearch);
  globalSearchRef.current = handleGlobalSearch;
  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && String(event.key).toLowerCase() === "k") {
        event.preventDefault();
        globalSearchRef.current();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.location.reload();
  };

  const LANGUAGE_OPTIONS = [
    { code: "pt-BR", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "ar", label: "عربي" },
  ];

  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const { getAll } = useSettings();
  useEffect(() => {
    async function fetchSettings() {
      try {
        const settings = await getAll();
        const enabledLanguagesSetting = settings.find(
          (s) => s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt-BR", "en"];
        try {
          if (enabledLanguagesSetting) {
            langs = JSON.parse(enabledLanguagesSetting);
          }
        } catch { }
        console.log(
          "Layout - enabledLanguages carregadas:",
          langs,
          "para companyId:",
          user?.companyId
        );
        setEnabledLanguages(langs);
      } catch (error) {
        console.log("Layout - erro ao carregar enabledLanguages:", error);
      }
    }
    fetchSettings();
  }, [user?.companyId]);

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    enabledLanguages.includes(lang.code)
  );

  // Trilha com os rótulos do menu; rota fora do menu cai no título antigo
  const { trailGroup, pageTitle } = useMemo(() => {
    const trail = getMenuTrail(location.pathname, (key) => i18n.t(key));
    return {
      trailGroup: trail.group,
      pageTitle: trail.title || getPageTitle(location.pathname),
    };
  }, [location.pathname]);
  const companyName = user?.company?.name;
  const planName = user?.company?.plan?.name;
  const planDue =
    user?.profile === "admin" && user?.company?.dueDate
      ? `${i18n.t("mainDrawer.appBar.user.active")} ${dateToClient(user.company.dueDate)}`
      : null;
  const profileRole = user?.profile === "admin" ? "Administrador" : "Atendente";
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
  const utilityControls = (
    <>
      <VersionControl
        onUpdateStart={handleUpdateStart}
        onUpdateComplete={handleUpdateComplete}
      />

      <div className={clsx(classes.languageSelector, "language-dropdown")}>
        <button
          type="button"
          aria-label="Selecionar idioma"
          onClick={() => setShowOptions(!showOptions)}
        >
          <UI_ICONS.language />
        </button>

        {showOptions && (
          <div>
            {filteredLanguageOptions.map((lang) => (
              <button
                type="button"
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
              >
                {lang.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <IconButton
        className={classes.toolbarButton}
        onClick={colorMode.toggleColorMode}
        aria-label="Alternar tema"
      >
        {theme.mode === "dark" ? <UI_ICONS.sun /> : <UI_ICONS.moon />}
      </IconButton>

      <NotificationsVolume setVolume={setVolume} volume={volume} />

      <IconButton
        onClick={handleRefreshPage}
        aria-label={i18n.t("mainDrawer.appBar.refresh")}
        className={classes.toolbarButton}
      >
        <UI_ICONS.refresh />
      </IconButton>
    </>
  );

  if (loading || updateInProgress) {
    return <BackdropLoading />;
  }

  return (
    <div className={clsx(classes.root, "logged-in-layout")}>
      {!hideMenu && (
        <Drawer
          variant={drawerVariant}
          className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
          classes={{
            paper: clsx(
              classes.drawerPaper,
              !isDrawerExpanded && classes.drawerPaperClose
            ),
          }}
          open={drawerVariant === "temporary" ? drawerOpen : true}
          onClose={drawerClose}
        >
          <div
            className={clsx(
              classes.toolbarIcon,
              !isDrawerExpanded && classes.toolbarIconCollapsed
            )}
          >
            <div className={classes.brandBox} aria-hidden="true">
              {theme.appLogoFavicon ? (
                <img src={theme.appLogoFavicon} alt="" />
              ) : (
                String(colorMode.appName || "D").trim().charAt(0).toUpperCase()
              )}
            </div>
            <div className={classes.brandText}>
              <p>{colorMode.appName}</p>
              {user?.company?.name && <span>{user.company.name}</span>}
            </div>
            <IconButton
              className={classes.drawerToggle}
              onClick={handleDrawerToggle}
              aria-label="Recolher menu lateral"
            >
              <UI_ICONS.caretLeft />
            </IconButton>
          </div>
          <List className={classes.containerWithScroll}>
            <MainListItems
              collapsed={!isDrawerExpanded}
              drawerClose={drawerClose}
            />
          </List>
          <div
            className={clsx(
              classes.drawerFooter,
              !isDrawerExpanded && classes.drawerFooterCollapsed
            )}
          >
            <button
              type="button"
              className={clsx(
                classes.drawerUserCard,
                !isDrawerExpanded && classes.drawerUserCardCollapsed
              )}
              onClick={handleMenu}
              aria-label="Abrir menu do usuário"
              title={!isDrawerExpanded ? `${user?.name || "Usuário"} · ${user?.email || ""}` : undefined}
            >
              <Avatar
                className={classes.drawerUserAvatar}
                src={profileUrl}
                alt={user?.name || ""}
              >
                {(user?.name || "").trim().charAt(0).toUpperCase()}
              </Avatar>
              <div className={classes.drawerUserInfo}>
                <p>{user?.name}</p>
                <span>{user?.email}</span>
              </div>
            </button>
            {isDrawerExpanded && (
              <span className={classes.drawerSecurity}>
                <UI_ICONS.shield aria-hidden="true" />
                Ambiente protegido
              </span>
            )}
          </div>
        </Drawer>
      )}

      <AppBar
        position="absolute"
        className={clsx(
          classes.appBar,
          !hideMenu &&
            (drawerOpen ? classes.appBarShift : classes.appBarCollapsed)
        )}
        color="default"
        elevation={0}
      >
        <Toolbar className={classes.toolbar}>
          {!hideMenu && (
            <IconButton
              edge="start"
              variant="contained"
              aria-label="Abrir menu lateral"
              onClick={handleDrawerToggle}
              className={clsx(classes.menuButton, drawerOpen && classes.menuButtonHidden)}
            >
              <UI_ICONS.menu />
            </IconButton>
          )}

          <div className={classes.titleBlock}>
            <Typography component="h1" color="inherit" className={classes.pageTitle} title={pageTitle}>
              {trailGroup && <span>{trailGroup}</span>}
              {trailGroup && <UI_ICONS.caretRight aria-hidden="true" />}
              <b>{pageTitle}</b>
            </Typography>
          </div>

          <div className={classes.searchArea}>
            <button
              type="button"
              className={classes.searchPill}
              onClick={handleGlobalSearch}
              aria-label="Buscar atendimentos e mensagens"
              aria-keyshortcuts="Control+K"
              title="Abrir busca de atendimentos"
            >
              <UI_ICONS.search />
              <span>Buscar atendimentos e mensagens</span>
              <kbd aria-hidden="true">{isMac ? "⌘ K" : "Ctrl K"}</kbd>
            </button>
          </div>

          {!hideMenu && (
            <div className={classes.toolbarActions}>
              {!isCompactViewport ? (
                <div className={classes.utilityActions}>{utilityControls}</div>
              ) : (
                <>
                  <IconButton
                    className={clsx(classes.toolbarButton, classes.utilityMoreButton)}
                    onClick={(event) => setUtilityAnchorEl(event.currentTarget)}
                    aria-label="Abrir mais opções"
                  >
                    <UI_ICONS.more />
                  </IconButton>
                  <Menu
                    id="utility-menu"
                    anchorEl={utilityAnchorEl}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    open={Boolean(utilityAnchorEl)}
                    onClose={() => {
                      setUtilityAnchorEl(null);
                      setShowOptions(false);
                    }}
                    PaperProps={{
                      className: classes.menuPaper,
                      style: { overflow: "visible" },
                    }}
                    MenuListProps={{ style: { padding: 0, overflow: "visible" } }}
                  >
                    <div className={classes.mobileUtilityMenu}>{utilityControls}</div>
                  </Menu>
                </>
              )}

              <div className={classes.communicationActions}>
                {user.id && <NotificationsPopOver volume={volume} />}
                <AnnouncementsPopover />
                <ChatPopover />
              </div>

              <span className={classes.actionsDivider} aria-hidden="true" />

              <button
                type="button"
                className={classes.profileButton}
                onClick={handleMenu}
                aria-label="Abrir menu do usuário"
              >
                <StyledBadge
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  variant="dot"
                >
                  <Avatar
                    alt={user?.name || ""}
                    className={classes.avatar2}
                    src={profileUrl}
                  >
                    {(user?.name || "").trim().charAt(0).toUpperCase()}
                  </Avatar>
                </StyledBadge>
                <span className={classes.profileMeta}>
                  <strong>{user?.name}</strong>
                  <span>{[profileRole, companyName].filter(Boolean).join(" · ")}</span>
                </span>
                <UI_ICONS.caretDown className={classes.profileChevron} aria-hidden="true" />
              </button>

              <UserModal
                open={userModalOpen}
                onClose={() => setUserModalOpen(false)}
                onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
                userId={user?.id}
              />

              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                open={menuOpen}
                onClose={handleCloseMenu}
                PaperProps={{
                  className: classes.menuPaper,
                  style: { width: 272, maxWidth: "calc(100vw - 24px)" },
                }}
              >
                {/* disabled: o MenuList pula o cabeçalho ao escolher o foco inicial */}
                <li className={classes.menuAccount} role="presentation" disabled>
                  <strong>{user?.name}</strong>
                  <span>{user?.email}</span>
                  {(companyName || planName) && (
                    <div className={classes.menuPlan}>
                      {companyName && <b title={companyName}>{companyName}</b>}
                      {planName && <span title={planName}>Plano {planName}</span>}
                      {planDue && <em>{planDue}</em>}
                    </div>
                  )}
                </li>
                <MenuItem onClick={handleOpenUserModal}>
                  <UI_ICONS.user />
                  {i18n.t("mainDrawer.appBar.user.profile")}
                </MenuItem>
                <MenuItem onClick={handleClickLogout} className={classes.menuDanger}>
                  <UI_ICONS.signOut />
                  {i18n.t("mainDrawer.appBar.user.logout")}
                </MenuItem>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                {selectedAnnouncement.text}
              </Typography>
              <FormControlLabel
                style={{ marginTop: 12 }}
                control={
                  <Checkbox
                    color="primary"
                    checked={ackChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAckChecked(checked);
                      handleToggleAcknowledge(selectedAnnouncement.id, checked);
                    }}
                  />
                }
                label="Estou ciente e não mostrar novamente"
              />
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              )}
              <Button
                onClick={() => setSelectedAnnouncement(null)}
                style={{ marginTop: 16 }}
                variant="outlined"
              >
                Voltar para lista
              </Button>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <NotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          Prioridade: {announcement.priority === 1 ? 'Alta' : announcement.priority === 2 ? 'Média' : 'Baixa'}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAnnouncementsModal(false)}
            color="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aniversário */}
      <BirthdayModal
        open={showBirthdayModal}
        onClose={() => setShowBirthdayModal(false)}
        user={user}
      />

      {/* Termos de uso: aceite obrigatório do admin (super nunca) */}
      <TermsAcceptModal />

    </div>
  );
};

export default LoggedInLayout;
